import { createSlice } from '@reduxjs/toolkit'
import { RootState, AppThunk } from './store'
import { get, postForm } from '../utils/rest'
import * as Toast from 'components/Toast'
import { Activity } from 'types/types'
import * as routes from '../assets/routes'
export interface ActivityState {
  isLoading: boolean
  activity: Activity | null
  isNotFound: boolean
}

const createInitialState = (): ActivityState => {
  return {
    isLoading: false,
    activity: null,
    isNotFound: false,
  }
}

export const activitySlice = createSlice({
  name: 'activity',
  initialState: createInitialState(),
  reducers: {
    fetchActivityBegin: (state) => {
      state.isLoading = true
    },
    fetchActivitySuccess: (state, action) => {
      state.isLoading = false
      state.activity = action.payload
      state.isNotFound = false
    },
    fetchActivityFail: (state) => {
      state.isLoading = false
      state.activity = null
      state.isNotFound = true
    },
    createActivtyBegin: (state) => {
      state.isLoading = true
    },
    createActivitySuccess: (state) => {
      state.isLoading = false
    },
    createActivtyFail: (state) => {
      state.isLoading = false
    },
    updateActivityStatusBegin: (state) => {
      state.isLoading = true
    },
    updateActivityStatusSuccess: (state) => {
      state.isLoading = false
    },
    updateActivityStatusFail: (state) => {
      state.isLoading = false
    },
    publishActivityBegin: (state) => {
      state.isLoading = true
    },
    publishActivitySuccess: (state) => {
      state.isLoading = false
    },
    publishActivityFail: (state) => {
      state.isLoading = false
    },
    unpublishActivityBegin: (state) => {
      state.isLoading = true
    },
    unpublishActivitySuccess: (state) => {
      state.isLoading = false
    },
    unpublishActivityFail: (state) => {
      state.isLoading = false
    },
    rejectActivityBegin: (state) => {
      state.isLoading = true
    },
    rejectActivitySuccess: (state) => {
      state.isLoading = false
    },
    rejectActivityFail: (state) => {
      state.isLoading = false
    },
    deleteActivityBegin: (state) => {
      state.isLoading = true
    },
    deleteActivitySuccess: (state) => {
      state.isLoading = false
    },
    deleteActivityFail: (state) => {
      state.isLoading = false
    },
    saveActivityBegin: (state) => {
      state.isLoading = true
    },
  },
})

export const {
  fetchActivityBegin,
  fetchActivitySuccess,
  fetchActivityFail,
  createActivtyBegin,
  createActivitySuccess,
  createActivtyFail,
  updateActivityStatusBegin,
  updateActivityStatusSuccess,
  updateActivityStatusFail,
  publishActivityBegin,
  publishActivitySuccess,
  publishActivityFail,
  unpublishActivityBegin,
  unpublishActivitySuccess,
  unpublishActivityFail,
  rejectActivityBegin,
  rejectActivitySuccess,
  rejectActivityFail,
  deleteActivityBegin,
  deleteActivitySuccess,
  deleteActivityFail,
  saveActivityBegin,
} = activitySlice.actions

export const loadActivityAsync =
  (containerpage: string | undefined, archive: string | undefined, post: string | undefined): AppThunk =>
  async (dispatch) => {
    dispatch(fetchActivityBegin())

    try {
      const res = await get<Activity>(`/api/activity/${containerpage}/${archive}/${post}`)
      if (res.status === 200) {
        dispatch(fetchActivitySuccess(res.parsedBody?.data as Activity))
      } else {
        Toast.dispatchToastError({ message: res.parsedBody?.message ?? 'Det gick inte att hämta aktiviteten' })
        dispatch(fetchActivityFail())
      }
    } catch {
      dispatch(fetchActivityFail())
    }
  }

export const fetchActivityByIdAsync =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(fetchActivityBegin())
    try {
      const res = await get<Activity>(`/api/activity/getactivity/${id}`)
      if (res.status === 200) {
        dispatch(fetchActivitySuccess(res.parsedBody?.data as Activity))
      } else {
        Toast.dispatchToastError({ message: res.parsedBody?.message ?? 'Det gick inte att hämta aktiviteten' })
        dispatch(fetchActivityFail())
      }
    } catch {
      Toast.dispatchToastError({ message: 'Fel vid hämtning av aktivitet' })
      dispatch(fetchActivityFail())
    }
  }

export const updateActivityStatusAsync =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(updateActivityStatusBegin())
    try {
      const res = await get<Activity>(`api/activity/updateactivitystatus/${id}`)
      if (res.status === 200 || res.status === 204) {
        dispatch(updateActivityStatusSuccess())
        Toast.dispatchToastSuccess({ message: 'Aktiviteten har skickats till granskning!' })
      } else {
        Toast.dispatchToastError({ message: 'Det gick inte att skicka aktiviteten till granskning' })
        dispatch(updateActivityStatusFail())
      }
    } catch (error) {
      Toast.dispatchToastError({ message: 'Fel vid skickats till granskning av aktivitet' })
      dispatch(updateActivityStatusFail())
    }
  }

export const publishActivityAsync =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(publishActivityBegin())
    try {
      const res = await get<Activity>(`/api/activity/publishActivity/${id}`)
      if (res.status === 200 || res.status === 204) {
        dispatch(publishActivitySuccess())
        Toast.dispatchToastSuccess({ message: res.parsedBody?.message ?? 'Aktiviteten har publicerats!' })
      } else {
        Toast.dispatchToastError({ message: res.parsedBody?.message ?? 'Det gick inte att publicera aktiviteten' })
        dispatch(publishActivityFail())
      }
    } catch (error) {
      Toast.dispatchToastError({ message: 'Fel vid publicering av aktivitet' })
      dispatch(publishActivityFail())
    }
  }

export const rejectActivityAsync =
  (id: string, reason: string): AppThunk =>
  async (dispatch) => {
    dispatch(rejectActivityBegin())
    try {
      const res = await get<Activity>(`/api/activity/rejectActivity/${id}/${reason}`)

      if (res.status === 200 || res.status === 204) {
        Toast.dispatchToastSuccess({ message: res.parsedBody?.message ?? 'Aktiviteten har nekats' })
        dispatch(rejectActivitySuccess())
      } else {
        Toast.dispatchToastError({ message: res.parsedBody?.message ?? 'Det gick inte att neka aktiviteten' })
        dispatch(rejectActivityFail())
      }
    } catch (error) {
      Toast.dispatchToastError({ message: 'Fel vid nekande av aktivitet' })
      dispatch(rejectActivityFail())
    }
  }

export const saveActivityAsync =
  (
    activity: Activity,
    activityMainImage: FileList | null,
    activitySecondaryImage: FileList | null,
    mainImageOriginal?: FileList | null,
    mainImageCropData?: string | null,
    secondaryImageOriginal?: FileList | null,
    secondaryImageCropData?: string | null,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(saveActivityBegin())

    try {
      const formData = new FormData()
      const mainImage = activityMainImage != null ? activityMainImage[0] : ''
      const secondaryImage = activitySecondaryImage != null ? activitySecondaryImage[0] : ''
      formData.append('activity', JSON.stringify(activity))
      formData.append('mainImage', mainImage)
      formData.append('secondaryImage', secondaryImage)

      if (mainImageOriginal && mainImageOriginal.length > 0) {
        formData.append('mainImageOriginal', mainImageOriginal[0])
      }
      if (mainImageCropData) {
        formData.append('mainImageCropData', mainImageCropData)
      }
      if (secondaryImageOriginal && secondaryImageOriginal.length > 0) {
        formData.append('secondaryImageOriginal', secondaryImageOriginal[0])
      }
      if (secondaryImageCropData) {
        formData.append('secondaryImageCropData', secondaryImageCropData)
      }

      const res = await postForm('/api/activity/createactivity', formData)

      if (res.status === 200 || (res.status === 204 && res.parsedBody)) {
        dispatch(createActivitySuccess())
        Toast.dispatchToastSuccess({ message: res.parsedBody?.message ?? 'Aktiviteten har sparats!' })

        window.location.replace(`${window.location.origin}${routes.EDIT_ACTIVITY}/${res.parsedBody?.data}`)
      } else {
        dispatch(createActivtyFail())
        Toast.dispatchToastError({
          message: res.parsedBody?.message ?? 'Det gick inte att spara aktiviteten',
          correlationId: res.parsedBody?.correlationId,
        })
      }
    } catch {
      dispatch(createActivtyFail())
      Toast.dispatchToastError({ message: 'Fel vid sparandet av aktiviteten' })
    }
  }

export const updateActivityAsync =
  (
    activity: Activity,
    activityMainImage: FileList | null,
    activitySecondaryImage: FileList | null,
    mainImageOriginal?: FileList | null,
    mainImageCropData?: string | null,
    secondaryImageOriginal?: FileList | null,
    secondaryImageCropData?: string | null,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(fetchActivityBegin())

    try {
      const formData = new FormData()
      const mainImage = activityMainImage != null && activityMainImage.length > 0 ? activityMainImage[0] : ''
      const secondaryImage =
        activitySecondaryImage != null && activitySecondaryImage.length > 0 ? activitySecondaryImage[0] : ''

      formData.append('activity', JSON.stringify(activity))
      formData.append('mainImage', mainImage)
      formData.append('secondaryImage', secondaryImage)

      if (mainImageOriginal && mainImageOriginal.length > 0) {
        formData.append('mainImageOriginal', mainImageOriginal[0])
      }
      if (mainImageCropData) {
        formData.append('mainImageCropData', mainImageCropData)
      }
      if (secondaryImageOriginal && secondaryImageOriginal.length > 0) {
        formData.append('secondaryImageOriginal', secondaryImageOriginal[0])
      }
      if (secondaryImageCropData) {
        formData.append('secondaryImageCropData', secondaryImageCropData)
      }

      const res = await postForm('/api/activity/updateactivity', formData)

      if (res.status === 200 || res.status === 204) {
        dispatch(createActivitySuccess())
        Toast.dispatchToastSuccess({ message: res.parsedBody?.message ?? 'Aktiviteten har uppdaterats!' })
        dispatch(fetchActivityByIdAsync(activity.id as string))
      } else {
        dispatch(createActivtyFail())
        Toast.dispatchToastError({
          message: res.parsedBody?.message ?? 'Det gick inte att uppdatera aktiviteten',
          correlationId: res.parsedBody?.correlationId,
        })
      }
    } catch {
      dispatch(createActivtyFail())
      Toast.dispatchToastError({ message: 'Fel vid uppdatering av aktivitet' })
    }
  }

export const deleteActivityAsync =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(deleteActivityBegin())
    try {
      const res = await get<Activity>(`/api/activity/deleteactivity/${id}`)
      if (res.status === 200 || res.status === 204) {
        Toast.dispatchToastSuccess({ message: res.parsedBody?.message ?? 'Aktivitet borttagen' })
        dispatch(deleteActivitySuccess())
      } else {
        Toast.dispatchToastError({
          message: res.parsedBody?.message ?? 'Det gick inte att ta bort aktiviteten',
          correlationId: res.parsedBody?.correlationId,
        })
        dispatch(deleteActivityFail())
      }
    } catch {
      Toast.dispatchToastError({ message: 'Fel vid borttagning av aktivitet' })
      dispatch(deleteActivityFail())
    }
  }

export const publishActivity =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(publishActivityBegin())
    try {
      const res = await get<Activity>(`/api/activity/publishactivity/${id}`)

      if (res.status === 200) {
        Toast.dispatchToastSuccess({ message: res.parsedBody?.message ?? 'Aktivitet publicerad' })
        dispatch(publishActivitySuccess())
      } else {
        Toast.dispatchToastError({
          message: res.parsedBody?.message ?? 'Det gick inte att ta bort aktiviteten',
          correlationId: res.parsedBody?.correlationId,
        })
        dispatch(publishActivityFail())
      }
    } catch {
      Toast.dispatchToastError({ message: 'Fel vid borttagning av aktivitet' })
      dispatch(publishActivityFail())
    }
  }

export const unpublishActivityAsync =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(unpublishActivityBegin())
    try {
      const res = await get<Activity>(`/api/activity/unpublishactivity/${id}`)

      if (res.status === 200 || res.status === 204) {
        Toast.dispatchToastSuccess({ message: res.parsedBody?.message ?? 'Aktivitet avpublicerad' })
        dispatch(unpublishActivitySuccess())
      } else {
        Toast.dispatchToastError({
          message: res.parsedBody?.message ?? 'Det gick inte att avpublicera aktiviteten',
          correlationId: res.parsedBody?.correlationId,
        })
        dispatch(unpublishActivityFail())
      }
    } catch {
      Toast.dispatchToastError({ message: 'Fel vid avpublicering av aktivitet' })
      dispatch(unpublishActivityFail())
    }
  }

export const selectActivityState = (state: RootState): ActivityState => state.activity
//export const activityState = (state: RootState): ActivityState => state.activity
export default activitySlice.reducer
